import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import Table from '../../../../component/table/Table';
import OdKpiRow from '../components/OdKpiRow';
import odTableStyles from '../components/odTableStyles';
import OdCoordCell from '../components/OdCoordCell';
import OdSelect from '../components/OdSelect';
import th from '../components/odColumnHeader';
import { buildTripNumbers, compactDuration } from '../odAnalyticsUtils';

const num = (value) => <span className="od-cell-num">{value}</span>;

const dash = (value) => (value === null || value === undefined || value === '' ? '—' : value);

/** One decimal, or null — never '0.0' for an absent figure. See OdEcoPill on the same trap. */
const fixed1 = (value) => (Number.isFinite(value) ? value.toFixed(1) : null);

const dur = (seconds) => (Number.isFinite(seconds) ? compactDuration(seconds) : '—');

/** U+2212 MINUS SIGN, as `buildFocus` uses: beside a digit a hyphen reads as a dash, not a sign. */
const MINUS = '−';

const KIND_LABELS = {
    TRIP: 'Trip', REFUEL: 'Refuel', DRAIN: 'Drain', CHARGE: 'Charge',
};

/** fleetconsole's `ledKindSel`. `''` is no filter. */
const KIND_OPTIONS = [
    { value: '', label: 'All entries' },
    { value: 'TRIP', label: 'Trips' },
    { value: 'REFUEL', label: 'Refuels' },
    { value: 'DRAIN', label: 'Drains / theft' },
    { value: 'CHARGE', label: 'Charge sessions' },
];

/** fleetconsole's `SRC` map, plus the two verdicts its prototype engine cannot produce. */
const FUEL_SOURCES = {
    LITRES: 'litres column (direct)',
    PERCENT_X_CAPACITY: 'fuel % × tank capacity',
    PERCENT_MAP_EQUATION: 'fuel % via map equation',
    GPS_ESTIMATION: 'GPS estimation counter',
    BLOCKED: 'percentage only — no tank capacity',
    NONE: 'no fuel signal',
};

/** `Trip #n` over the visible trip list, or the honest fallback when the trip is filtered out. */
const tripLabel = (tripId, tripNumbers) => {
    const number = tripNumbers.get(tripId);
    return number === undefined ? null : `Trip #${number}`;
};

const entryLabel = (row, tripNumbers) => {
    if (row.kind === 'TRIP') return tripLabel(row.tripId, tripNumbers) || 'Trip';
    if (row.kind === 'REFUEL') return row.micro ? 'Micro-refill' : 'Refuel';
    return KIND_LABELS[row.kind] || row.kind;
};

/**
 * The chronological fuel and energy ledger — fleetconsole's Route Atlas → Energy ledger tab.
 *
 * Rows come from GET /v3/trip/analytics (`data.ledger`), already merged, ordered and totalled by
 * the server. The client re-derives none of it: the running Σ columns are per device and getting
 * them wrong is the easiest way to ship a plausible-looking wrong number, which is exactly why
 * they are not computed here.
 */
export const ledgerColumns = ({ showAsset, tripNumbers, zone }) => [
    showAsset && {
        id: 'asset',
        name: th('Asset'),
        selector: (row) => row.assetName,
        sortable: true,
        minWidth: '8rem',
        cell: (row) => (
            <div className="od-cell-stack">
                <span className="od-cell-strong">{row.assetName}</span>
                <span className="od-cell-muted">{row.deviceSerial || '—'}</span>
            </div>
        ),
    },
    {
        id: 'time',
        name: th('Time', 'When the entry began, in the agency timezone.'),
        selector: (row) => row.startTime,
        sortable: true,
        minWidth: '11rem',
        cell: (row) => (row.startTime ? moment.tz(row.startTime, zone).format('DD-MM-YYYY HH:mm:ss') : '—'),
    },
    {
        id: 'entry',
        name: th('Entry', 'What happened: a trip, a refuel, a drain, or a charging session.'),
        selector: (row) => row.kind,
        sortable: true,
        minWidth: '10rem',
        cell: (row) => (
            <div className="od-cell-stack">
                <span className="od-cell-entry">
                    <span className={`od-badge od-badge-${String(row.kind).toLowerCase()}`}>
                        {entryLabel(row, tripNumbers)}
                    </span>
                    {/* Flagged, not dropped — see the note under the table. */}
                    {row.implausible && <span className="od-badge od-badge-warn">implausible</span>}
                </span>
                <span className="od-cell-muted">
                    {dur(row.durationSec)}
                    {row.implausible && Number.isFinite(row.pctPerMin)
                        ? ` · ${row.pctPerMin} %/min` : ''}
                </span>
            </div>
        ),
    },
    {
        id: 'km',
        name: th('km', 'Distance covered. Trips only — an event has no distance.'),
        selector: (row) => row.km ?? -1,
        sortable: true,
        right: true,
        minWidth: '5rem',
        cell: (row) => num(dash(fixed1(row.km))),
    },
    {
        id: 'litres',
        name: th('Fuel L', 'Litres burnt on a trip, taken on at a refuel, or lost to a drain.'),
        selector: (row) => row.litres ?? -1,
        sortable: true,
        right: true,
        minWidth: '5.5rem',
        cell: (row) => {
            const value = fixed1(row.litres);
            if (value === null) return num('—');
            if (row.kind === 'REFUEL') return num(`+${value}`);
            if (row.kind === 'DRAIN') return num(`${MINUS}${value}`);
            return num(value);
        },
    },
    {
        id: 'kwh',
        name: th('kWh', 'Energy measured off the vehicle\'s own cumulative counter, per trip. A charging session reports percent, not kWh — no battery pack size is configured anywhere in Horizon.'),
        selector: (row) => row.kwh ?? -1,
        sortable: true,
        right: true,
        minWidth: '5rem',
        cell: (row) => num(dash(fixed1(row.kwh))),
    },
    {
        id: 'level',
        name: th('Level / SOC', 'Tank level either side of the entry, or state of charge for a charging session.'),
        selector: (row) => row.levelAfterL ?? row.endPct ?? -1,
        sortable: true,
        right: true,
        minWidth: '8rem',
        cell: (row) => {
            if (row.kind === 'CHARGE') {
                return num(Number.isFinite(row.startPct) && Number.isFinite(row.endPct)
                    ? `${Math.round(row.startPct)}→${Math.round(row.endPct)}%` : '—');
            }
            if (row.kind === 'TRIP') {
                return num(Number.isFinite(row.startL) && Number.isFinite(row.endL)
                    ? `${fixed1(row.startL)}→${fixed1(row.endL)} L` : '—');
            }
            const before = fixed1(row.levelBeforeL);
            const after = fixed1(row.levelAfterL);
            if (after === null) return num('—');
            // Both ends when the server sends them, which is what makes the row checkable against
            // the tank — `9.0→31.0 L` reconciles with a +22.0 L fill, `→31.0 L` cannot be checked
            // at all. The bare arrow remains the fallback for a server that predates `levelBeforeL`
            // rather than deriving the start from `after - litres`: the two ends are measured off
            // the smoothed series while the delta accumulates raw steps, so the subtraction would
            // quietly disagree with the reading beside it.
            return num(before === null ? `→${after} L` : `${before}→${after} L`);
        },
    },
    {
        id: 'runningL',
        name: th('Σ L', 'Running total of litres burnt by this asset, in ledger order. Per device — never a fleet-wide total.'),
        selector: (row) => row.runningL ?? -1,
        sortable: true,
        right: true,
        minWidth: '5rem',
        cell: (row) => num(row.runningL > 0 ? fixed1(row.runningL) : '—'),
    },
    {
        id: 'runningKwh',
        name: th('Σ kWh', 'Running total of measured trip energy for this asset. Charging is not added — it is reported in percent.'),
        selector: (row) => row.runningKwh ?? -1,
        sortable: true,
        right: true,
        minWidth: '5.5rem',
        cell: (row) => num(row.runningKwh > 0 ? fixed1(row.runningKwh) : '—'),
    },
    {
        id: 'location',
        name: th('Location', 'Where the entry was recorded. Click the row to see it on the Route Map, the coordinates to open Google Maps, or ⧉ to copy them.'),
        selector: (row) => row.lat,
        sortable: true,
        grow: 2,
        minWidth: '13rem',
        cell: (row) => <OdCoordCell lat={row.lat} lon={row.lon} address={row.location} />,
    },
].filter(Boolean);

/**
 * Fleet totals from the per-asset rollups the server already computed.
 *
 * The per-100 figures divide by the distance of the trips that **produced a figure**, which the
 * server holds and does not send. It is recoverable exactly — `km = 100 × fuelL / lPer100` — so the
 * fleet number is a sum of the server's own denominators rather than a second, looser rule that
 * would understate consumption for an asset whose fuel signal dropped out mid-window.
 */
export const ledgerTotals = (assets) => {
    const list = Array.isArray(assets) ? assets : [];
    const sum = (pick) => list.reduce((total, asset) => total + (Number(pick(asset)) || 0), 0);
    const measured = (valueKey, rateKey) => {
        const rows = list.filter((asset) => Number.isFinite(asset[valueKey])
            && Number.isFinite(asset[rateKey]) && asset[rateKey] > 0);
        if (rows.length === 0) return null;
        const km = rows.reduce((total, asset) => total + (100 * asset[valueKey]) / asset[rateKey], 0);
        return km > 0 ? (100 * rows.reduce((t, asset) => t + asset[valueKey], 0)) / km : null;
    };

    return {
        km: sum((asset) => asset.km),
        // Null rather than 0 when no asset produced a figure: a fleet with no fuel signal has not
        // burnt nothing, and the tile must not say it did.
        fuelL: list.some((asset) => Number.isFinite(asset.fuelL)) ? sum((asset) => asset.fuelL) : null,
        tripKwh: list.some((asset) => Number.isFinite(asset.tripKwh)) ? sum((asset) => asset.tripKwh) : null,
        lPer100: measured('fuelL', 'lPer100'),
        kwhPer100: measured('tripKwh', 'kwhPer100'),
        refuelL: sum((asset) => asset.refuelL),
        drainL: sum((asset) => asset.drainL),
        chargeDeltaPct: sum((asset) => asset.chargeDeltaPct),
        microRefuels: sum((asset) => asset.microRefuels),
    };
};

/** The one fuel source the whole fan-out shares, or null when the assets disagree. */
const sameSource = (assets) => {
    const sources = [...new Set((assets || []).map((asset) => asset.fuelSource).filter(Boolean))];
    return sources.length === 1 ? sources[0] : null;
};

const LedgerView = (props) => {
    const {
        entries, assets, rollup, availability, trips, timezone, error, tooLarge, onOpenEntry,
    } = props;

    const zone = timezone || 'UTC';
    const [kindFilter, setKindFilter] = useState('');
    const rows = useMemo(() => (Array.isArray(entries) ? entries : []), [entries]);
    const assetRows = useMemo(() => (Array.isArray(assets) ? assets : []), [assets]);
    const visible = useMemo(
        () => (kindFilter ? rows.filter((row) => row.kind === kindFilter) : rows),
        [rows, kindFilter],
    );
    const tripNumbers = useMemo(() => buildTripNumbers(trips), [trips]);
    const showAsset = useMemo(() => new Set(rows.map((row) => row.assetName)).size > 1, [rows]);
    const totals = useMemo(() => ledgerTotals(assetRows), [assetRows]);

    const columns = useMemo(
        () => ledgerColumns({ showAsset, tripNumbers, zone }),
        [showAsset, tripNumbers, zone],
    );

    // fleetconsole gates its fuel tiles on the powertrain. Horizon has no powertrain field on this
    // response, and does not need one: an asset with no fuel signal reports a null figure and an
    // ICE asset reports no counter, which is the same question answered by the data itself.
    const showFuel = totals.fuelL !== null || rollup?.refuels > 0 || rollup?.drains > 0;
    const showEnergy = totals.tripKwh !== null || rollup?.charges > 0;

    const tiles = [
        {
            key: 'distance',
            label: 'Distance',
            value: `${Math.round(totals.km)} km`,
            sub: `${rollup?.trips ?? 0} trips`,
        },
        showFuel && {
            key: 'burnt',
            label: 'Fuel burnt',
            value: totals.fuelL === null ? '—' : `${totals.fuelL.toFixed(1)} L`,
            sub: totals.lPer100 === null ? 'no per-100 basis' : `${totals.lPer100.toFixed(1)} L/100 km`,
        },
        showFuel && {
            key: 'refuelled',
            label: 'Refuelled',
            value: `${Math.round(totals.refuelL)} L`,
            sub: `${rollup?.refuels ?? 0} events${totals.microRefuels ? ` · ${totals.microRefuels} micro` : ''}`,
        },
        showFuel && {
            key: 'drains',
            label: 'Drain / theft alerts',
            value: rollup?.drains ?? 0,
            sub: rollup?.drains
                ? `${Math.round(totals.drainL)} L unexplained — review`
                : 'none detected',
            tone: rollup?.drains ? 'bad' : 'ok',
        },
        showEnergy && {
            key: 'energy',
            label: 'Energy used',
            value: totals.tripKwh === null ? '—' : `${Math.round(totals.tripKwh)} kWh`,
            sub: totals.kwhPer100 === null
                ? 'measured from the counter'
                : `${totals.kwhPer100.toFixed(1)} kWh/100 km — measured`,
        },
        showEnergy && {
            key: 'charged',
            label: 'Charged',
            // Percent, not kWh. fleetconsole shows an estimated kWh here by multiplying the SOC
            // rise by a configured pack size; no pack size exists on a Horizon device document, so
            // the honest figure is the one the BMS actually reported.
            value: `+${Math.round(totals.chargeDeltaPct)}%`,
            sub: `${rollup?.charges ?? 0} sessions${rollup?.implausibleCharges ? ` · ${rollup.implausibleCharges} implausible` : ''}`,
            tone: rollup?.implausibleCharges ? 'warn' : undefined,
        },
    ].filter(Boolean);

    const emptyState = () => {
        if (error) {
            return {
                title: tooLarge ? 'Window too large' : 'Unable to load the energy ledger',
                text: error,
            };
        }
        // `blocked` counts as a signal: the device reports a percentage and only the tank capacity
        // is missing, which is a configuration answer rather than "nothing came back".
        const measured = (value) => value === 'available' || value === 'blocked';
        if (measured(availability?.fuel) || measured(availability?.charge)) {
            return {
                title: 'No ledger entries in this window',
                text: 'Telemetry covered this window; no trip, refuel, drain or charging session'
                    + ' fell inside it.',
            };
        }
        return {
            title: 'No fuel or energy signal in this window',
            text: 'No fuel level, state of charge or energy counter came back for the selected'
                + ' period, so nothing can be ledgered. A zero here would mean the signal is'
                + ' missing — not that nothing was burnt.',
        };
    };

    const empty = rows.length === 0 ? emptyState() : null;
    const filtered = visible.length !== rows.length;
    const source = sameSource(assetRows);
    // A mixed fleet gets one honest chip rather than the first asset's source standing in for all
    // of them — the whole point of the chip is that it says how the litres were arrived at.
    const mixed = assetRows.length > 1 ? 'mixed fuel sources' : null;
    const sourceLabel = source ? (FUEL_SOURCES[source] || source) : mixed;

    return (
        <div className="od-view">
            <OdKpiRow tiles={tiles} />

            <div className="od-card">
                <div className="od-card-header">
                    <div className="od-card-header-text">
                        <div className="od-card-title">
                            Fuel &amp; energy ledger
                            <span className="od-badge od-badge-neutral">
                                {filtered ? `${visible.length} of ${rows.length}` : `${rows.length}`}
                            </span>
                            {sourceLabel && (
                                <span className="od-badge od-badge-neutral">{sourceLabel}</span>
                            )}
                        </div>
                        <div className="od-card-subtitle">
                            Every trip, refuel, drain and charging session in one chronological
                            stream, with running totals per asset
                        </div>
                    </div>
                    <div className="od-card-actions">
                        <OdSelect
                          label="Show"
                          value={kindFilter}
                          onChange={setKindFilter}
                          options={KIND_OPTIONS}
                          ariaLabel="Filter by entry kind"
                        />
                    </div>
                </div>

                {empty ? (
                    <div className="od-empty">
                        <div className="od-empty-title">{empty.title}</div>
                        <div className="od-empty-text">{empty.text}</div>
                    </div>
                ) : (
                    <div className="od-card-body od-table-wrap">
                        <Table
                          tableData={visible}
                          columns={columns}
                          module="odAnalytics"
                          // A trip row selects its trip on the map; an event row pins its point.
                          onRowClicked={onOpenEntry}
                          highlightOnHover
                          pointerOnHover
                          pagination={visible.length > 10}
                          paginationServer={false}
                          paginationPerPage={10}
                          handlePageChange={() => {}}
                          handlePerRowsChange={() => {}}
                          setSelectedRowCount={() => {}}
                          totalRows={visible.length}
                          customStyles={odTableStyles}
                          backgroundColor="#F9FAFB"
                        />
                    </div>
                )}

                <div className="od-card-note">
                    Trip litres are reconciled: the level drop across the trip, plus any refuel and
                    minus any drain that happened inside it. A refuel or charge is tied to a trip
                    only when it sits inside one, or when its window is short enough to have clipped
                    exactly one — a long window touching several trips is reported as ambiguous
                    rather than billed to the first of them. Charging is reported in percent because
                    no battery pack size is configured, so Σ kWh counts measured trip energy only. A
                    session marked implausible rose faster than a charger plausibly can — usually a
                    tracker reconnecting and reporting its true level — and is kept in the totals so
                    this table agrees with the fuel report.
                </div>
            </div>
        </div>
    );
};

export default LedgerView;
